/**
 * I2 — Scalable product lookup index.
 *
 * Foundation layer for scaling to 1,000,000+ products. Provides:
 *   - O(1) part-number → slug lookup (Map)
 *   - Normalized part-number matching (strips dashes, spaces, case)
 *   - Prefix index for autocomplete
 *   - Brand + category faceted counts
 *
 * At current scale the entire catalog fits in memory. As the catalog grows
 * past ~50k items, swap the in-memory Maps for a lazy-loaded IndexedDB
 * mirror (interface below stays identical) or move lookups server-side
 * to a Postgres function keyed on trigram/GIN indexes. Callers should
 * only ever consume the exports from this module — never touch the raw
 * source arrays — so the storage layer stays swappable.
 */

export interface IndexedProduct {
  slug: string;
  partNumber: string;
  brand?: string;
  category?: string;
  title?: string;
}

const normalize = (s: string): string =>
  s.toLowerCase().replace(/[\s\-_/.]/g, "");

class ProductIndex {
  private byNormalized = new Map<string, IndexedProduct>();
  private bySlug = new Map<string, IndexedProduct>();
  private prefixIndex = new Map<string, IndexedProduct[]>(); // 3-char prefix
  private byBrand = new Map<string, IndexedProduct[]>();
  private byCategory = new Map<string, IndexedProduct[]>();
  private loaded = false;

  /** Bulk-load or hot-swap the index. Idempotent across HMR. */
  load(products: IndexedProduct[]): void {
    this.byNormalized.clear();
    this.bySlug.clear();
    this.prefixIndex.clear();
    this.byBrand.clear();
    this.byCategory.clear();

    for (const p of products) {
      const norm = normalize(p.partNumber);
      this.byNormalized.set(norm, p);
      this.bySlug.set(p.slug, p);

      const prefix = norm.slice(0, 3);
      if (prefix.length >= 2) {
        const bucket = this.prefixIndex.get(prefix);
        if (bucket) bucket.push(p);
        else this.prefixIndex.set(prefix, [p]);
      }

      if (p.brand) {
        const bucket = this.byBrand.get(p.brand);
        if (bucket) bucket.push(p);
        else this.byBrand.set(p.brand, [p]);
      }
      if (p.category) {
        const bucket = this.byCategory.get(p.category);
        if (bucket) bucket.push(p);
        else this.byCategory.set(p.category, [p]);
      }
    }
    this.loaded = true;
  }

  /** Exact or normalized part-number lookup. O(1). */
  findByPartNumber(partNumber: string): IndexedProduct | undefined {
    return this.byNormalized.get(normalize(partNumber));
  }

  findBySlug(slug: string): IndexedProduct | undefined {
    return this.bySlug.get(slug);
  }

  /** Prefix autocomplete search. Capped at `limit` to keep UI responsive. */
  searchByPrefix(query: string, limit = 20): IndexedProduct[] {
    const norm = normalize(query);
    if (norm.length < 2) return [];
    const prefix = norm.slice(0, 3);
    const candidates = this.prefixIndex.get(prefix) ?? [];
    const out: IndexedProduct[] = [];
    for (const c of candidates) {
      if (normalize(c.partNumber).startsWith(norm)) {
        out.push(c);
        if (out.length >= limit) break;
      }
    }
    return out;
  }

  countByBrand(brand: string): number {
    return this.byBrand.get(brand)?.length ?? 0;
  }

  countByCategory(category: string): number {
    return this.byCategory.get(category)?.length ?? 0;
  }

  size(): number {
    return this.bySlug.size;
  }

  isLoaded(): boolean {
    return this.loaded;
  }
}

// Singleton — one index per browser tab. Safe under HMR because
// re-imports return the same module instance.
export const productIndex = new ProductIndex();

/** Hydrate the index from Shopify catalog fetch. Call once at app boot. */
export function hydrateProductIndex(products: IndexedProduct[]): void {
  if (products.length === 0) return;
  productIndex.load(products);
}